/**
 * Regras do grimório (techspec-grimorio.md §04.1). Sem React, sem i18n:
 * o nome no idioma da tela chega por `nomeDe`. Testado em tests/grimorio.mjs.
 */
import { CONSUMIVEIS, ehGratis, esperaConsumivelMs, grupoConsumivel, type ConsumivelId, type GrupoConsumivel } from './consumiveis';
import { chanceDeSucesso, cicloMs, ehMagiaPronta, PAGINAS, type Magia, type MagiaId } from './magias';
export { ehMagiaPronta };
export { aplicarMagiasDaTabela } from './magias';
export { TABELA_PADRAO } from './combateTabela';

export type Filtro = 'todas' | 'ataque' | 'debuff' | 'cura' | 'buff' | 'viagem';
export type Tipo = Exclude<Filtro, 'todas'>;
export type Estado = 'cheia' | 'parcial' | 'longe' | 'sem-magia' | 'indisponivel';

export const FILTROS: readonly Filtro[] = ['todas', 'ataque', 'debuff', 'cura', 'buff', 'viagem'];

/** A ordem do livro (página, posição) — que já é a ordem de Magia exigida. */
export const LIVRO: readonly Magia[] = PAGINAS.flat();

export function tipoDaMagia(m: Magia): Tipo | null {
  if (m.id === 'recall') return 'viagem';            // sem `kind` no cliente; no servidor é SpellKind.Recall
  if (m.kind === 'damage' || m.kind === 'poison' || m.kind === 'paralyze') return 'ataque';
  if (m.kind === 'heal' || m.kind === 'cure' || m.kind === 'resurrect') return 'cura';
  // techspec-magias-de-efeito
  if (m.kind === 'buff') return 'buff';
  if (m.kind === 'debuff' || m.kind === 'dispel') return 'debuff';
  return null;
}

/** Sem acento, sem caixa, espaços colapsados: "  VÁS   flam" casa "Vas Flam". */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export interface Consulta {
  readonly busca: string;
  readonly filtro: Filtro;
  readonly todas: boolean;
  /** `nomeDaMagia`, injetado: o módulo não depende do catálogo de idioma. */
  nomeDe(id: MagiaId): string;
}

export function casa(m: Magia, busca: string, nomeLocal: string): boolean {
  const q = normalizar(busca);
  return !q || normalizar(`${nomeLocal} ${m.nomeEn} ${m.palavras}`).includes(q);
}

function passa(m: Magia, c: Consulta, filtro: Filtro): boolean {
  return (c.todas || ehMagiaPronta(m.id))
    && (filtro === 'todas' || tipoDaMagia(m) === filtro)
    && casa(m, c.busca, c.nomeDe(m.id));
}

export function visiveis(c: Consulta): Magia[] {
  return LIVRO.filter(m => passa(m, c, c.filtro));
}

/** A contagem de cada filtro com a busca e o "mostrar indisponíveis" de agora. */
export function contagens(c: Consulta): Record<Filtro, number> {
  const out: Record<Filtro, number> = { todas: 0, ataque: 0, debuff: 0, cura: 0, buff: 0, viagem: 0 };
  for (const f of FILTROS) out[f] = LIVRO.filter(m => passa(m, c, f)).length;
  return out;
}

/** Filtro sem nenhuma magia pronta não aparece (Viagem some enquanto a tabela não chega). */
export function filtrosComMagia(): Filtro[] {
  return FILTROS.filter(f => f === 'todas' || LIVRO.some(m => ehMagiaPronta(m.id) && tipoDaMagia(m) === f));
}

export function contarProntas(): number {
  return LIVRO.filter(m => ehMagiaPronta(m.id)).length;
}

/** Nada entre as prontas, mas a busca casa alguma indisponível — a mensagem avisa. */
export function soIndisponiveis(c: Consulta): boolean {
  return !c.todas && visiveis(c).length === 0
    && LIVRO.some(m => !ehMagiaPronta(m.id) && casa(m, c.busca, c.nomeDe(m.id)));
}

/** As 8 magias de um círculo têm a mesma faixa (magias.ts:104-167). */
export function faixaDoCirculo(p: number): { min: number; full: number } {
  const m = PAGINAS[Math.min(8, Math.max(1, p)) - 1][0];
  return { min: m.skillMin, full: m.skillFull };
}

export interface Circulo {
  readonly circulo: number;
  readonly min: number;
  readonly full: number;
  readonly magias: readonly Magia[];
}

export function porCirculo(ms: readonly Magia[]): Circulo[] {
  const out: Circulo[] = [];
  for (let p = 1; p <= PAGINAS.length; p++) {
    const magias = ms.filter(m => m.pagina === p);
    if (magias.length) out.push({ circulo: p, ...faixaDoCirculo(p), magias });
  }
  return out;
}

/** `chance` é null quando não há o que mostrar (indisponível ou perfil sem Magia). */
export function estadoDaMagia(m: Magia, magery: number | null): { estado: Estado; chance: number | null } {
  if (!ehMagiaPronta(m.id)) return { estado: 'indisponivel', chance: null };
  if (magery == null) return { estado: 'sem-magia', chance: null };
  const c = chanceDeSucesso(magery, m.skillMin, m.skillFull);
  return { estado: c >= 1 ? 'cheia' : c > 0 ? 'parcial' : 'longe', chance: c };
}

/**
 * Tempo até poder conjurar de novo, com o bônus do jogador.
 */
export function conjuraMs(m: Magia, velMagia = 0): number {
  return cicloMs(m.id, velMagia);
}

export type Efeito =
  | { readonly tipo: 'dano'; readonly min: number; readonly max: number; readonly atrasoMs: number }
  | { readonly tipo: 'mente' | 'veneno' | 'antidoto' | 'cura' | 'recall' | 'paralisia' }
  /** Buff ou debuff (techspec-magias-de-efeito): o texto é por magia. */
  | { readonly tipo: 'efeito'; readonly id: string };

export function efeitoDe(m: Magia): Efeito | null {
  if (m.id === 'recall') return { tipo: 'recall' };
  switch (m.kind) {
    case 'poison': return { tipo: 'veneno' };
    case 'cure': return { tipo: 'antidoto' };
    case 'heal': return { tipo: 'cura' };
    case 'paralyze': return { tipo: 'paralisia' };
    case 'buff':
    case 'debuff':
    case 'dispel':
    case 'resurrect': return { tipo: 'efeito', id: m.id };
    case 'damage':
      // Golpe Mental: 0/0 na tabela; o dano sai da Int (CombatMath.cs:73-79).
      return m.danoMin && m.danoMax
        ? { tipo: 'dano', min: m.danoMin, max: m.danoMax, atrasoMs: m.atrasoMs ?? 0 }
        : { tipo: 'mente' };
    default: return null;
  }
}

export interface ItemDoGrimorio {
  readonly id: ConsumivelId;
  readonly qtd: number;
  readonly gratis: boolean;
  readonly esperaMs: number;
  readonly grupo: GrupoConsumivel;
}

/** Os cinco, sempre. Vida e mana menores não têm pilha. */
export function itensDoGrimorio(mochila: Readonly<Record<string, number>> | undefined): ItemDoGrimorio[] {
  return CONSUMIVEIS.map(id => ({
    id,
    qtd: mochila?.[id] ?? 0,
    gratis: ehGratis(id),
    esperaMs: esperaConsumivelMs(id),
    grupo: grupoConsumivel(id)!
  }));
}

/** Roving tabindex: a linha que recebe o Tab — a ativa se ainda está visível, senão a primeira. */
export function paradaDeTab(ids: readonly string[], ativo: string | null): string | null {
  return ativo && ids.includes(ativo) ? ativo : ids[0] ?? null;
}

/** ↑ ↓ Home End. `'busca'` = subir para a busca (↑ na primeira). `null` = tecla não é daqui. */
export function navegar(ids: readonly string[], atual: string | null, tecla: string): string | 'busca' | null {
  if (!ids.length) return null;
  const i = atual ? ids.indexOf(atual) : -1;
  switch (tecla) {
    case 'Home': return ids[0];
    case 'End': return ids[ids.length - 1];
    case 'ArrowDown': return i < 0 ? ids[0] : ids[Math.min(ids.length - 1, i + 1)];
    case 'ArrowUp': return i <= 0 ? 'busca' : ids[i - 1];
    default: return null;
  }
}
